'use client';
import {styled} from '@mui/material/styles';
import {useEffect, useRef} from 'react';
import {MediaType, type MediaAsset} from '../types';

// Both media kinds fill the frame identically; only the element and the
// `object-fit` differ, so the base style object is shared.
const mediaStyles = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
} as const;

const fitStyle = ({$fit}: {$fit: MediaFit}) => ({...mediaStyles, objectFit: $fit});
const withFit = {shouldForwardProp: (propName: string) => propName !== '$fit'} as const;
const Image = styled('img', withFit)<{$fit: MediaFit}>(fitStyle);
const Video = styled('video', withFit)<{$fit: MediaFit}>(fitStyle);

type MediaFit = 'cover' | 'contain';

export interface HeroGallerySlideProps {
  asset: MediaAsset;
  muted: boolean;
  paused?: boolean;
  fit?: MediaFit;
}

export function HeroGallerySlide({asset, muted, paused, fit = 'cover'}: HeroGallerySlideProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Read from the IntersectionObserver and paused-prop effects below without
  // pulling either into the other's dep array (both would re-run the observer
  // setup unnecessarily).
  const inViewRef = useRef(false);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  // React doesn't reliably reflect the `muted` prop to the DOM property, so set
  // it imperatively whenever the toggle changes.
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted;
    }
  }, [muted]);

  // Pause when the hero scrolls out of view (saves decode work and stops audio
  // playing off-screen once unmuted); resume when it returns. Keyed on the asset
  // so a video mounting after an image slide (videoRef was null on first mount)
  // is still observed. Sync `muted` from the prop here too so the freshly-mounted
  // element plays muted (autoplay policy) without a hardcoded `muted` attribute.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return undefined;
    }
    video.muted = muted;
    const observer = new IntersectionObserver(
      ([entry]) => {
        inViewRef.current = entry.isIntersecting;
        if (entry.isIntersecting && !pausedRef.current) {
          void video.play().catch(() => undefined);
        } else {
          video.pause();
        }
      },
      {threshold: 0.25},
    );
    observer.observe(video);
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.src, asset.type]);

  // The fullscreen lightbox mounts its own autoplaying VideoPlayer for this
  // asset, so this inline copy must stop while it's open, and only resume if
  // still in view once it closes.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (paused) {
      video.pause();
    } else if (inViewRef.current) {
      void video.play().catch(() => undefined);
    }
  }, [paused]);

  if (asset.type === MediaType.Video) {
    return <Video ref={videoRef} $fit={fit} src={asset.src} poster={asset.poster} autoPlay loop playsInline aria-label={asset.alt} />;
  }

  return <Image $fit={fit} src={asset.src} alt={asset.alt ?? ''} />;
}

HeroGallerySlide.displayName = 'HeroGallerySlide';
